import { bundledTemplateFiles } from '../generated/templates'
import { parseTemplate } from './parser'
import type { FormTemplate } from './types'

/**
 * The templates the app can offer: the ones bundled at build time from the
 * shared `templates/` folder, plus any the user imported.
 *
 * An imported template with the same id replaces the bundled one, so a form can
 * be corrected on set without waiting for a deploy.
 */

const KEY_IMPORTED = 'consent.importedTemplates'

export interface LoadedTemplate {
  key: string
  fileName: string
  bundled: boolean
  template: FormTemplate | null
  error: string | null
}

function readImported(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(KEY_IMPORTED) ?? '{}') as Record<string, string>
  } catch {
    return {}
  }
}

function writeImported(all: Record<string, string>): void {
  localStorage.setItem(KEY_IMPORTED, JSON.stringify(all))
}

export function loadTemplates(): LoadedTemplate[] {
  const byId = new Map<string, LoadedTemplate>()

  for (const file of bundledTemplateFiles) {
    try {
      const template = parseTemplate(file.source)
      byId.set(template.id, {
        key: template.id,
        fileName: file.fileName,
        bundled: true,
        template,
        error: null,
      })
    } catch (e) {
      // A broken bundled template is a build error, and the test suite catches
      // it before deploy. Do not surface it mid-shoot.
      console.error(`Bundled template ${file.fileName} is unusable`, e)
    }
  }

  for (const [fileName, source] of Object.entries(readImported())) {
    try {
      const template = parseTemplate(source)
      byId.set(template.id, {
        key: template.id,
        fileName,
        bundled: false,
        template,
        error: null,
      })
    } catch (e) {
      byId.set(fileName, {
        key: fileName,
        fileName,
        bundled: false,
        template: null,
        error: (e as Error).message,
      })
    }
  }

  return [...byId.values()]
}

export function availableTemplates(): FormTemplate[] {
  return loadTemplates()
    .map((t) => t.template)
    .filter((t): t is FormTemplate => t !== null)
}

export function templateById(id: string): FormTemplate | undefined {
  return availableTemplates().find((t) => t.id === id)
}

/**
 * Stores an imported template. Parses first, so a malformed file is rejected at
 * import rather than at 04:30 with someone waiting to sign.
 */
export function importTemplate(source: string): FormTemplate {
  const template = parseTemplate(source)
  const all = readImported()
  // Keyed by template id, not by the name of the file it arrived in, so
  // re-importing a corrected version replaces the old one rather than stacking
  // up beside it.
  all[`${template.id}.md`] = source
  writeImported(all)
  return template
}

export function removeImported(fileName: string): void {
  const all = readImported()
  delete all[fileName]
  delete all[`${fileName}.md`]
  writeImported(all)
}
